import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { Marker, Tooltip } from 'react-leaflet'
import L from 'leaflet'

import type { EntityDragPayload } from '../entityDrag'

import type { EntityCategory, MappableEntity } from './controlCenterMapTypes'
import type { OperationInputs, OperatorDef, OperatorId } from '../operations'
import { entityRef } from '../operations'

import { createEntityIcon, createInvisiblePopupAnchorIcon } from './mapMarkerIcons'
import { operatorDocksPopupToCorner, MapEntityPopupShell } from './MapEntityPopups'
import { useMapEntityDrag } from './useEntityDrag'
import { OperatorPopupControls, useOperatorPopupState } from './OperatorPopupControls'

export function EntityMapMarker({
  entity,
  selected,
  onSelect,
  operators = [],
  onRunOperator,
  operationRunning = false,
  destinationOptions = [],
  preferredDestinationRef = '',
  openPopupToken = 0,
  operationResultOperatorId = null,
  suppressPopup = false,
  hidePin = false,
}: {
  entity: MappableEntity
  selected: boolean
  onSelect: (id: string, category: EntityCategory) => void
  operators?: OperatorDef[]
  onRunOperator?: (
    entity: MappableEntity,
    operatorId: OperatorId,
    inputs: OperationInputs,
  ) => void
  /** Called as soon as popup args change — clear/update the active step before re-run. */
  onInputsEdit?: (inputs: OperationInputs) => void
  operationRunning?: boolean
  destinationOptions?: Array<{ id: string; label: string; group?: string; keywords?: string }>
  preferredDestinationRef?: string
  /** Bumped on explicit focus (omni badge) so the popup re-opens like a map click. */
  openPopupToken?: number
  /** When set to a look-around op, dock the popup after that result has been run. */
  operationResultOperatorId?: OperatorId | null
  /** Mobile: skip the Leaflet/docked info popup (ops live in the output panel). */
  suppressPopup?: boolean
  /**
   * Hide the visible pin (e.g. coincident cluster still shows the count badge)
   * while keeping an invisible popup anchor.
   */
  hidePin?: boolean
}) {
  const markerRef = useRef<L.Marker | null>(null)
  const zBase = entity.category === 'person' ? 400 : entity.category === 'asset' ? 200 : 0
  const dragPayload = useMemo((): EntityDragPayload | null => {
    if (hidePin) return null
    return {
      refId: entityRef(entity.category, entity.id),
      label: entity.title,
      color: entity.color,
    }
  }, [entity.category, entity.id, entity.title, entity.color, hidePin])
  const suppressClickRef = useMapEntityDrag({
    markerRef,
    payload: dragPayload,
    remountKey: `${selected ? '1' : '0'}:${hidePin ? '1' : '0'}`,
  })
  const dockToCorner = operatorDocksPopupToCorner(operationResultOperatorId)
  const icon = useMemo(
    () =>
      hidePin
        ? createInvisiblePopupAnchorIcon()
        : createEntityIcon(entity.color, entity.icon, selected),
    [entity.color, entity.icon, selected, hidePin],
  )

  const onRun = useCallback(
    (op: OperatorDef, inputs: OperationInputs) => {
      onRunOperator?.(entity, op.id, inputs)
    },
    [entity, onRunOperator],
  )

  const {
    activeOperatorId,
    activeOperator,
    draftInputs,
    popupDismissed,
    lockPopupForEntityDrop,
    dismissPopup,
    revealPopup,
    selectOperator,
    patchInputs,
    selectDestinationEntity,
    runActiveOperator,
    destinationOptional,
    needsDestination,
    canManualRun,
    showRunBesideRadius,
    showRunBesideRange,
    showRunAtEnd,
    isRerun,
    runLabel,
  } = useOperatorPopupState({
    operators: suppressPopup ? [] : operators,
    preferredDestinationRef,
    operationResultOperatorId,
    operationRunning,
    isActive: selected && !suppressPopup,
    openPopupToken,
    onRun: !suppressPopup && onRunOperator ? onRun : undefined,
  })

  useEffect(() => {
    const marker = markerRef.current
    if (!marker) return
    if (suppressPopup || !selected || popupDismissed || dockToCorner) {
      marker.closePopup()
      return
    }
    marker.openPopup()
    // Re-open after camera settle / drag remount so badge focus matches a map click.
    const t = window.setTimeout(() => {
      markerRef.current?.openPopup()
    }, 560)
    return () => window.clearTimeout(t)
  }, [selected, openPopupToken, dockToCorner, popupDismissed, suppressPopup])

  const categoryLabel =
    entity.category === 'job'
      ? 'Job'
      : entity.category === 'person'
        ? 'Person'
        : entity.category === 'asset'
          ? 'Asset'
          : entity.category === 'municipality'
            ? 'Municipality'
            : 'Utility'

  return (
    <Marker
      ref={markerRef}
      position={{ lat: entity.lat, lng: entity.lng }}
      icon={icon}
      interactive={!hidePin}
      zIndexOffset={hidePin ? 100 : selected ? 800 : zBase}
      eventHandlers={
        hidePin
          ? undefined
          : {
              click: () => {
                if (suppressClickRef.current) return
                if (!suppressPopup) revealPopup()
                onSelect(entity.id, entity.category)
              },
            }
      }
    >
      {hidePin ? null : (
        <Tooltip direction="top" offset={[0, -14]} opacity={0.96}>
          <div className="max-w-[14rem]">
            <div className="text-xs font-semibold leading-tight">{entity.title}</div>
            {entity.subtitle ? (
              <div className="mt-0.5 text-[10px] leading-snug opacity-80">{entity.subtitle}</div>
            ) : null}
          </div>
        </Tooltip>
      )}
      {suppressPopup ? null : (
        <MapEntityPopupShell
          docked={dockToCorner}
          open={selected && !popupDismissed}
          lockOpen={lockPopupForEntityDrop}
          minWidth={280}
          onDismiss={dismissPopup}
        >
          <div className="min-w-[16rem] max-w-[20rem]">
            <div className="flex items-start gap-2">
              <span
                className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: entity.color }}
                aria-hidden
              />
              <div className="min-w-0">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-base-content/45">
                  {categoryLabel}
                </div>
                <div className="text-sm font-semibold leading-tight text-base-content">
                  {entity.title}
                </div>
                {entity.subtitle ? (
                  <div className="mt-1 text-xs leading-snug text-base-content/70">{entity.subtitle}</div>
                ) : null}
                {entity.detail ? (
                  <div className="mt-1 text-xs leading-snug text-base-content/55">{entity.detail}</div>
                ) : null}
              </div>
            </div>
            {operators.length > 0 && onRunOperator ? (
              <OperatorPopupControls
                variant="entity"
                operators={operators}
                operationRunning={operationRunning}
                destinationOptions={destinationOptions}
                activeOperatorId={activeOperatorId}
                activeOperator={activeOperator}
                draftInputs={draftInputs}
                selectOperator={selectOperator}
                patchInputs={patchInputs}
                selectDestinationEntity={selectDestinationEntity}
                runActiveOperator={runActiveOperator}
                destinationOptional={destinationOptional}
                needsDestination={needsDestination}
                canManualRun={canManualRun}
                showRunBesideRadius={showRunBesideRadius}
                showRunBesideRange={showRunBesideRange}
                showRunAtEnd={showRunAtEnd}
                isRerun={isRerun}
                runLabel={runLabel}
              />
            ) : null}
          </div>
        </MapEntityPopupShell>
      )}
    </Marker>
  )
}
